<script setup>
import { ref, onMounted } from 'vue';

const props = defineProps({
    exitUrl: { type: String, required: true },
    generateUrl: { type: String, required: true },
    pollUrlPrefix: { type: String, required: true },
    conversationUrl: { type: String, required: true },
    newConversationUrl: { type: String, required: true },
    conversationId: { type: [String, Number], default: null },
});

const messages = ref([]);
const context = ref({});
const currentConversationId = ref(props.conversationId);
const isGenerating = ref(false);
const pollTimer = ref(null);
const confirmedFilters = ref(null);

const loadConversation = async () => {
    try {
        const response = await fetch(props.conversationUrl);
        const data = await response.json();
        currentConversationId.value = data.id;
        messages.value = data.messages || [];
        context.value = data.context || {};

        // Check if already confirmed
        if (data.context?.confirmed_filters) {
            confirmedFilters.value = data.context.confirmed_filters;
        }

        // Check last message for confirmed state
        const lastMsg = messages.value[messages.value.length - 1];
        if (lastMsg?.confirmed) {
            confirmedFilters.value = lastMsg.filters_used;
        }
    } catch (e) {
        console.error('Failed to load conversation', e);
    }
};

const sendMessage = async (text) => {
    if (isGenerating.value) return;

    messages.value.push({ role: 'user', content: text });
    isGenerating.value = true;

    try {
        const response = await fetch(props.generateUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
            body: JSON.stringify({ prompt: text }),
        });

        const data = await response.json();
        currentConversationId.value = data.conversation_id;
        startPolling();
    } catch (e) {
        console.error('Failed to send message', e);
        isGenerating.value = false;
    }
};

const startPolling = () => {
    if (pollTimer.value) clearInterval(pollTimer.value);

    pollTimer.value = setInterval(async () => {
        try {
            const response = await fetch(`${props.pollUrlPrefix}${currentConversationId.value}/poll`);
            const data = await response.json();

            if (data.status === 'completed') {
                stopPolling();
                isGenerating.value = false;

                messages.value.push({
                    role: 'assistant',
                    content: data.message,
                    search_results: data.search_results,
                    filters_used: data.filters_used,
                    confirmed: data.confirmed,
                });

                if (data.confirmed && data.filters_used) {
                    confirmedFilters.value = data.filters_used;
                }
            } else if (data.status === 'failed' || data.status === 'budget_exceeded') {
                stopPolling();
                isGenerating.value = false;

                messages.value.push({
                    role: 'assistant',
                    content: data.status === 'budget_exceeded'
                        ? _mft('lead-hunter:hugo.budgetExceeded')
                        : _mft('lead-hunter:hugo.errorMessage'),
                });
            }
        } catch (e) {
            console.error('Polling failed', e);
        }
    }, 2000);
};

const stopPolling = () => {
    if (pollTimer.value) {
        clearInterval(pollTimer.value);
        pollTimer.value = null;
    }
};

const startNewConversation = async () => {
    try {
        const response = await fetch(props.newConversationUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
        });

        const data = await response.json();
        currentConversationId.value = data.id;
        messages.value = [];
        context.value = {};
        confirmedFilters.value = null;
    } catch (e) {
        console.error('Failed to create new conversation', e);
    }
};

const createCampaign = () => {
    if (!confirmedFilters.value) return;

    const params = new URLSearchParams();
    params.set('filters', JSON.stringify(confirmedFilters.value));
    window.location.href = route('lead-hunter.campaign.create')+`?${params.toString()}`;
};

onMounted(() => {
    loadConversation();
});
</script>

<template>
    <div class="flex flex-col h-screen bg-gray-50">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
            <div class="flex items-center gap-3">
                <a :href="exitUrl" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
                    </svg>
                </a>
                <div class="flex items-center gap-2">
                    <div class="w-8 h-8 rounded-full bg-aiAgentLeadFinder flex items-center justify-center">
                        <ai-agent-lead-finder-avatar class="w-full h-full" />
                    </div>
                    <div>
                        <h1 class="text-sm font-semibold text-gray-900">
                            <mf-translate domainkey="lead-hunter:hugo.title"></mf-translate>
                        </h1>
                        <p class="text-xs text-gray-500 mb-0">
                            <mf-translate domainkey="lead-hunter:hugo.subtitle"></mf-translate>
                        </p>
                    </div>
                </div>
            </div>
            <button-secondary
                @click="startNewConversation"
                size="sm"
            >
                <mf-translate domainkey="lead-hunter:hugo.newSearch"></mf-translate>
            </button-secondary>
        </div>

        <!-- Main content area -->
        <div class="flex flex-1 overflow-hidden">
            <!-- Chat -->
            <div class="flex-1 flex flex-col" :class="confirmedFilters ? 'border-r border-gray-200' : ''">
                <hugo-chat
                    :messages="messages"
                    :is-generating="isGenerating"
                    @send="sendMessage"
                ></hugo-chat>
            </div>

            <!-- Filter panel (shown when confirmed) -->
            <div v-if="confirmedFilters" class="w-80 flex-shrink-0">
                <hugo-filter-panel
                    :filters="confirmedFilters"
                    @create-campaign="createCampaign"
                ></hugo-filter-panel>
            </div>
        </div>
    </div>
</template>
